'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { JaText, PersonaMenu } from '@/components/shell'
import { openCommandPalette } from '@/components/CommandPalette'
import { AUTO_NAME, breadcrumbsFor, crumbLabelsVersion, subscribeCrumbLabels } from '@/lib/navigation'
import { AutopilotWaitChip } from './AutopilotWaitChip'
import { ContinueNoticeLine } from './ContinueNoticeLine'
import { NeedsYouBell, SearchButton, StoryClockPill } from './HeaderControls'

interface HeaderProps {
  onOpenMobileMenu?: () => void
}

export function Header({ onOpenMobileMenu }: HeaderProps) {
  const pathname = usePathname()
  // Re-render when a record page names its crumb ("WB-101").
  React.useSyncExternalStore(subscribeCrumbLabels, crumbLabelsVersion, crumbLabelsVersion)
  const breadcrumbs = breadcrumbsFor(pathname)
  const current = breadcrumbs[breadcrumbs.length - 1]

  // One document title per page: "司令室 Shireishitsu · Disaster Management AI Office" (WCAG 2.4.2).
  React.useEffect(() => {
    document.title = `${current.label} · ${AUTO_NAME}`
  }, [current.label])

  return (
    <header
      className={cn(
        'fixed right-4 top-4 z-sticky',
        'left-4 md:left-[calc(var(--shell-rail-width)+1rem)]',
        'rounded-2xl bg-card/80 backdrop-blur-xl',
        'border border-card/60 ring-1 ring-foreground/[0.03]',
        'shadow-float',
        'flex items-center justify-between gap-3',
        'h-14 px-3 lg:px-5',
        'transition-[left] duration-300 ease-out'
      )}
    >
      <div className='flex min-w-24 flex-1 items-center gap-2 lg:min-w-32 xl:min-w-48'>
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={onOpenMobileMenu}
          className='-ml-1 text-muted-foreground hover:text-foreground md:hidden'
          aria-label='Open navigation menu'
        >
          <Icons.menu className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
        </Button>

        <nav aria-label='Breadcrumb' className='hidden min-w-0 sm:block'>
          <ol className='flex min-w-0 items-center gap-1.5 text-sm'>
            {/* The product name repeats the rail; below 2xl the page crumbs and the story chip need the room. */}
            <li className='hidden shrink-0 text-muted-foreground 2xl:block'>{AUTO_NAME}</li>
            {breadcrumbs.map((crumb, index) => {
              const last = index === breadcrumbs.length - 1
              return (
                <li key={crumb.href} className='flex min-w-0 items-center gap-1.5'>
                  <span aria-hidden='true' className={cn('text-muted-foreground', index === 0 && 'hidden 2xl:inline')}>
                    /
                  </span>
                  {last ? (
                    <span aria-current='page' className='truncate font-medium text-foreground'>
                      <JaText text={crumb.label} />
                    </span>
                  ) : (
                    <Link
                      href={crumb.href}
                      className='truncate rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    >
                      <JaText text={crumb.label} />
                    </Link>
                  )}
                </li>
              )
            })}
          </ol>
        </nav>
        <span title={current.label} className='truncate text-sm font-medium text-foreground sm:hidden'>
          <JaText text={current.label} />
        </span>
      </div>

      {/* Shrinks through the story chip's label only; every other control keeps its size. */}
      <div className='flex min-w-0 shrink items-center justify-end gap-2 [&>*:not(.shrink)]:shrink-0'>
        <StoryClockPill />
        <AutopilotWaitChip />

        {/* The full search field only on the widest screens, so the breadcrumb and the story chip keep their room at 1440. */}
        <div className='hidden 2xl:block'>
          <SearchButton />
        </div>
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={openCommandPalette}
          className='text-muted-foreground hover:text-foreground 2xl:hidden'
          aria-label='Search pages and records'
        >
          <Icons.search className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
        </Button>

        {/* Below xl the persona name truncates sooner (the label already hides there). */}
        <PersonaMenu className='hidden sm:flex [&_select]:max-w-[7.5rem] xl:[&_select]:max-w-[13rem]' />
        <NeedsYouBell />
      </div>
      <ContinueNoticeLine />
    </header>
  )
}
